import React, { FC, useEffect, useRef } from 'react';
import { Box, Tab, Tabs, Typography } from '@mui/material';
import { useMobileHeaderConfig, MobileHeaderTabsConfig } from './MobileHeaderContext';
import { TAB_SWIPE_EVENT, TabSwipeDetail } from './SwipeTabViews';

/**
 * Header tabs whose indicator tracks the swipe gesture: SwipeTabViews broadcasts
 * its fractional position and this moves the MUI indicator directly (no React
 * re-render per frame), interpolating between the tab buttons' rects. On settle
 * the CSS transition is restored, and MUI's own value-driven update lands on the
 * same coordinates, so there's no jump.
 */
const HeaderTabs: FC<{ config: MobileHeaderTabsConfig }> = ({ config }) => {
  const tabsRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const onSwipe = (e: Event) => {
      const { position, animate } = (e as CustomEvent<TabSwipeDetail>).detail;
      const rootEl = tabsRef.current;
      if (!rootEl) return;
      const indicator = rootEl.querySelector<HTMLElement>('.MuiTabs-indicator');
      const tabs = rootEl.querySelectorAll<HTMLElement>('[role="tab"]');
      if (!indicator || tabs.length === 0) return;

      const clamped = Math.min(tabs.length - 1, Math.max(0, position));
      const i0 = Math.floor(clamped);
      const i1 = Math.min(tabs.length - 1, Math.ceil(clamped));
      const t = clamped - i0;
      const a = tabs[i0];
      const b = tabs[i1];
      const left = a.offsetLeft + (b.offsetLeft - a.offsetLeft) * t;
      const width = a.offsetWidth + (b.offsetWidth - a.offsetWidth) * t;

      indicator.style.transition = animate ? '' : 'none';
      indicator.style.left = `${left}px`;
      indicator.style.width = `${width}px`;
    };

    window.addEventListener(TAB_SWIPE_EVENT, onSwipe);
    return () => window.removeEventListener(TAB_SWIPE_EVENT, onSwipe);
  }, []);

  return (
    <Tabs
      ref={tabsRef}
      value={config.value}
      onChange={(_event, value) => config.onChange(value)}
      variant={config.variant ?? 'fullWidth'}
      scrollButtons={false}
      allowScrollButtonsMobile
      sx={{
        minHeight: 44,
        px: 0.5,
        '& .MuiTab-root': { minHeight: 44, py: 0 },
        ...config.sx,
      }}
    >
      {config.tabs.map(tab => (
        <Tab key={String(tab.value)} value={tab.value} label={tab.label} />
      ))}
    </Tabs>
  );
};

/**
 * Renders the current page's header content (tabs or heading) inside the shared
 * mobile top bar. It sits within the AppBar's glass with no separator, so it
 * reads as part of the header.
 */
const MobileHeaderSlot: FC = () => {
  const config = useMobileHeaderConfig();
  if (!config) return null;

  if (config.type === 'tabs') {
    return <HeaderTabs config={config} />;
  }

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        px: 2,
        pt: 0.25,
        pb: 1.25,
        ...config.sx,
      }}
    >
      <Typography
        component="div"
        sx={{
          flex: 1,
          minWidth: 0,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          fontFamily: theme => theme.tokens.typography.displayFontFamily,
          fontSize: '18px',
          lineHeight: 1.2,
        }}
      >
        {config.title}
      </Typography>
      {config.action}
    </Box>
  );
};

export default MobileHeaderSlot;
